/**
 * Custom menu frontend compatibility for ScandiPWA
 * @copyright Scandiweb, Inc. All rights reserved.
 */

import { MenuComponent } from 'Component/Menu/Menu.component';
import { MenuContainer } from 'Component/Menu/Menu.container';
import MenuItemComponent from 'Component/MenuItem';
import { Mods, ReactElement } from 'Type/Common.type';
import { getFirstLevelWithChild } from 'Util/DesktopMenu';
import MenuHelper, { getSortedItems } from 'Util/Menu';
import { MenuItemType, MenuLocation } from 'Util/Menu/Menu.type';

import { FormattedMenuItem, Menu, MenuItem } from '../type/Menu.type';
import { getCustomMenuQuery } from './ScandipwaMenuQuery.plugin';

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/renderDesktopSubLevelItems */
export const renderDesktopSubLevelItems = (
    item: FormattedMenuItem,
    mods: Mods,
    instance: MenuComponent,
): ReactElement => {
    const { item_id } = item;
    const { closeMenu, activeMenuItemsStack } = instance.props;

    return (
        <MenuItemComponent
          activeMenuItemsStack={ activeMenuItemsStack }
          item={ item }
          itemMods={ mods }
          closeMenu={ closeMenu }
          isLink
          key={ item_id }
        />
    );
};

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/renderDesktopSubLevelWithItemClass */
export const renderDesktopSubLevelWithItemClass = (
    [category]: [FormattedMenuItem],
    _callback: void,
    instance: MenuComponent,
): ReactElement => {
    const { device } = instance.props;
    const { children, item_class, item_id } = category;
    const childrenArray = getSortedItems(Object.values(children));

    if (device.isMobile || !childrenArray.length) {
        return null;
    }

    const isLogo = item_class === 'Menu-ItemFigure_type_logo';
    const mods = {
        isLogo: !!isLogo,
    };

    return (
        <div
          block="Menu"
          elem="SubLevelDesktop"
          key={ item_id }
        >
            <div
              block="Menu"
              elem="ItemList"
              mods={ mods }
            >
                { childrenArray.map((item) => renderDesktopSubLevelItems(item, mods, instance)) }
            </div>
        </div>
    );
};

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/getMenuOptions */
export const getMenuOptions = (): { identifier: string } => {
    const {
        header_content: {
            header_menu = '',
        } = {},
    } = window.contentConfiguration || {};

    return {
        identifier: header_menu || 'new-main-menu',
    };
};

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/getCustomMenu */
export const getCustomMenu = (_args: unknown[], _callback: void, instance: MenuContainer): void => {
    const { device } = instance.props;

    instance.fetchData<{ menu: Menu }>(
        [getCustomMenuQuery(getMenuOptions())],
        ({ menu: { items = [] } = {} }) => instance.setState({
            menu: MenuHelper.reduce(items),
            activeMenuItemsStack: device.isMobile ? [] : getFirstLevelWithChild(MenuHelper.reduce(items)),
        }),
        () => false,
        false,
    );
};

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/getMenuUrl */
export const getMenuUrl = (
    { url, url_type, category_id }: Pick<MenuItem, 'url' | 'url_type' | 'category_id'>,
): MenuLocation | string => {
    switch (url_type) {
    case MenuItemType.TYPE_CATEGORY:
        return {
            pathname: url,
            search: '',
            state: { category: category_id },
        };
    case MenuItemType.TYPE_CMS_PAGE:
        return {
            pathname: url,
            search: '',
            state: { page: true },
        };
    default:
        return url;
    }
};

/** @namespace Scandiweb/ScandipwaMenu/Plugin/Menu/Plugin/getCustomMenuData */
export const getCustomMenuData = ([menuItem]: [MenuItem]): FormattedMenuItem => {
    const {
        cms_page_identifier,
        url,
        url_type,
        category_id,
        ...item
    } = menuItem;

    return {
        ...item,
        url: getMenuUrl({ url, url_type, category_id }),
        children: {},
    };
};

export default {
    'Component/Menu/Component': {
        'member-function': {
            renderDesktopSubLevel: renderDesktopSubLevelWithItemClass,
        },
    },
    'Component/Menu/Container': {
        'member-function': {
            _getMenu: getCustomMenu,
        },
    },
    'Util/Menu': {
        'member-function': {
            getMenuData: getCustomMenuData,
        },
    },
};
